import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconsModule } from '@thx/ui/icons';
import { NzRowDirective } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { concatMap, Subject, takeUntil } from 'rxjs';
import { IChatResponse } from '../../interfaces/chat-response.interface';
import { ChatService } from '../../services/chat/chat.service';

@Component({
  selector: 'chatbot-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconsModule, NzIconModule, NzRowDirective],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.less',
})
export class ChatInputComponent implements OnInit, AfterViewInit, OnDestroy {
  messages = signal<IChatResponse[]>([]);
  form!: FormGroup;

  textarea = viewChild.required<ElementRef>('textarea');
  send = output<IChatResponse>();
  isLoadingChanged = output<boolean>();
  abort = output<void>();

  isAborted = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  private destroy$ = new Subject<void>();
  private abortController: AbortController | null = null;

  constructor(
    private readonly _renderer: Renderer2,
    private readonly _formBuilder: FormBuilder,
    private readonly _chatService: ChatService,
    private readonly ngZone: NgZone,
  ) {
    this.form = this._formBuilder.group({
      message: ['', [Validators.required, Validators.pattern(/\S+/)]],
    });
  }

  ngOnInit(): void {
    sessionStorage.setItem('sessionId', crypto.randomUUID());
    this.isLoadingChanged.emit(this.isLoading());
  }

  ngAfterViewInit(): void {
    this.focus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  get userInput(): string {
    return this.form.get('message')?.value || '';
  }

  set userInput(value: string) {
    this.form.patchValue({ message: value });
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterPress(e: Event): void {
    if ((e as KeyboardEvent).ctrlKey || (e as KeyboardEvent).metaKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  focus(): void {
    const textarea = this.textarea();
    if (textarea?.nativeElement) {
      this._renderer.selectRootElement(textarea.nativeElement).focus();
    }
  }

  abortProcessing() {
    this.abort.emit();
    this.isAborted.set(true);
    this.focus();
  }

  sendMessage(): void {
    if (!this.form.valid || this.isLoading()) {
      return;
    }

    const userMessage = this.userInput.trim();
    this.userInput = '';
    this.focus();

    // Add user message to chat
    this.messages.update((msgs) => [
      ...msgs,
      {
        message: userMessage,
        isBot: false,
        timestamp: new Date(),
      },
    ]);

    // Emit the message
    this.send.emit({
      message: userMessage,
      isBot: false,
    });

    this.isLoading.set(true);
    this.isLoadingChanged.emit(true);
    this.isAborted.set(false);

    let botResponse = '';
    this.abortController = new AbortController();
    const sessionId = sessionStorage.getItem('sessionId') || crypto.randomUUID();

    // Stream the response with character-by-character animation
    this._chatService
      .streamMessageWithFetch(userMessage, sessionId, this.abortController.signal)
      .pipe(
        // Add small delay between characters for visual effect (optional)
        concatMap((chunk) => {
          // Adjust delay (in ms) for faster/slower typing effect
          // 0 = instant, 30-50 = typing effect, 100+ = slower
          return new Promise((resolve) => {
            this.ngZone.runOutsideAngular(() => {
              setTimeout(() => {
                this.ngZone.run(() => resolve(chunk));
              }, 10); // Change to 30 for typing effect
            });
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (chunk) => {
          if (!this.isAborted()) {
            botResponse += chunk;

            // Update the last message (bot response) in real-time
            this.messages.update((msgs) => {
              const updatedMessages = [...msgs];
              const lastMsg = updatedMessages[updatedMessages.length - 1];

              const formatted = botResponse;
              if (lastMsg?.isBot) {
                lastMsg.message = formatted;
              } else {
                updatedMessages.push({
                  message: formatted,
                  isBot: true,
                  timestamp: new Date(),
                });
              }
              return updatedMessages;
            });

            // Emit progressive bot response to parent (for streaming in ChatComponent)
            this.send.emit({
              message: botResponse,
              isBot: true,
            });
          }
        },
        error: (error) => {
          if (!this.isAborted()) {
            console.error('Stream error:', error);
            this.messages.update((msgs) => [
              ...msgs,
              {
                message: 'Error: Failed to get response. Please try again.',
                isBot: true,
                timestamp: new Date(),
              },
            ]);
          }
          this.isLoading.set(false);
          this.isLoadingChanged.emit(false);
        },
        complete: () => {
          if (!this.isAborted()) {
            this.send.emit({
              message: botResponse,
              isBot: true,
            });
          }
          this.isLoading.set(false);
          this.isLoadingChanged.emit(false);
          this.focus();
        },
      });
  }

  abortMessage(): void {
    this.isAborted.set(true);
    if (this.abortController) {
      this.abortController.abort();
    }
    this.isLoading.set(false);
    this.isLoadingChanged.emit(false);
    this.abort.emit();
  }

  autoResize() {
    const textarea = this.textarea().nativeElement;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const messagesContainer = document.querySelector('.messages-list');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 0);
  }
}
